"use client";

import { ArrowRight, Flame, Wind, Mountain, Snowflake, Brain, Waves, Feather, Sun, Candy } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OptimizationSolutions() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const classes = [{
    code: "§ R.01",
    icon: Flame,
    title: "OTA REVENUE MANAGEMENT",
    description: "Strategic management of your OTA listings to maximize visibility, conversion rates, and net revenue through data-driven pricing.",
    duration: "STRATEGY"
  }, {
    code: "§ R.02",
    icon: Wind,
    title: "TECH STACK CONSULTING",
    description: "Expert recommendations and seamless implementation of hospitality tech stacks to streamline operations and boost efficiency.",
    duration: "IMPLEMENTATION"
  }, {
    code: "§ R.03",
    icon: Mountain,
    title: "REVENUE AUDITS",
    description: "Comprehensive analysis of your current revenue performance to identify untapped opportunities and growth bottlenecks.",
    duration: "ANALYSIS"
  }, {
    code: "§ R.04",
    icon: Snowflake,
    title: "DIRECT BOOKING GROWTH",
    description: "Proven strategies to shift bookings from high-commission OTAs to your direct website, increasing your profit margins.",
    duration: "GROWTH"
  }, {
    code: "§ R.05",
    icon: Brain,
    title: "DYNAMIC PRICING",
    description: "Real-time market analysis and automated pricing adjustments to ensure your rates are always competitive and profitable.",
    duration: "OPTIMIZATION"
  }, {
    code: "§ R.06",
    icon: Waves,
    title: "RATE PARITY",
    description: "Continuous monitoring and correction of rate discrepancies across all distribution channels to protect your brand integrity.",
    duration: "PROTECTION"
  }, {
    code: "§ R.07",
    icon: Feather,
    title: "OTA LISTING OPTIMIZATION",
    description: "Enhancing your property content, imagery, and descriptions to rank higher and convert more guests on major platforms.",
    duration: "VISIBILITY"
  }, {
    code: "§ R.08",
    icon: Sun,
    title: "DIGITAL MARKETING",
    description: "Integrated digital marketing campaigns designed to drive qualified traffic and increase direct hotel bookings.",
    duration: "MARKETING"
  }, {
    code: "§ R.09",
    icon: Candy,
    title: "REVENUE INTEGRATION",
    description: "Aligning your digital marketing efforts with revenue management goals for a unified, high-conversion strategy.",
    duration: "SYNERGY"
  }];
  return <section data-section-id="1291" id="optimizationsolutions" className="bg-background text-foreground py-24 md:py-44 px-6 md:px-12 relative overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute top-20 right-0 md:right-12 select-none font-default leading-none text-foreground/[0.04]" style={{
      fontSize: "clamp(18rem, 32vw, 36rem)"
    }}>
        01
      </div>

      <div className="max-w-[1440px] mx-auto relative">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28 transition-all duration-[520ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-4 mb-10">
              <span className="font-default text-xs md:text-sm uppercase tracking-[0.18em] text-foreground font-medium">
                § 01 / REVENUE EXCELLENCE
              </span>
              <span className="block w-10 h-px bg-foreground" />
            </div>
            <h2 className="font-default uppercase text-foreground leading-[0.86]" style={{
            fontSize: "clamp(3rem, 8vw, 7.5rem)",
            letterSpacing: "-0.015em"
          }}>
              MAXIMIZE{" "}
              <span className="font-default italic text-secondary" style={{
              letterSpacing: "0.01em"
            }}>
                REVENUE
              </span>
            </h2>
          </div>

          {}
          <div className="lg:col-span-8 lg:pt-4 flex flex-col justify-end">
            <p className="font-default text-muted-foreground max-w-2xl mb-8" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em"
          }}>
              Nine pillars of revenue management. One goal: maximizing your property's profitability. Our data-driven strategies are engineered to optimize your OTA presence, tech stack, and direct booking performance.
            </p>
            <div className="flex items-center gap-3">
              <span className="block w-2 h-2 bg-secondary" />
              <span className="font-default text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium">
                — 09 CORE SERVICES · DATA-DRIVEN RESULTS
              </span>
            </div>
          </div>
        </div>

        {}
        <div className="relative mb-16 md:mb-20">
          <div className="h-px w-full bg-border" />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {classes.map((cls, i) => <ClassCard key={cls.title} data-index={i} index={i} code={cls.code} Icon={cls.icon} title={cls.title} description={cls.description} duration={cls.duration} />)}
        </div>
      </div>
    </section>;
}
function ClassCard({
  index,
  code,
  Icon,
  title,
  description,
  duration,
  ...rest
}: {
  index: number;
  code: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  duration: string;
  "data-index"?: number;
}) {
  const reveal = useRevealOnScroll<HTMLDivElement>();
  const delay = index % 3 * 80 + Math.floor(index / 3) * 40;
  return <div {...rest} ref={reveal.ref} className={`group relative bg-background p-8 md:p-10 transition-all duration-[520ms] ease-out hover:bg-card ${reveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionDelay: reveal.isVisible ? `${delay}ms` : "0ms"
  }}>
      {}
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-secondary transition-all duration-300 ease-out" style={{
        width: "12px",
        height: "1.5px"
      }} />
        <span className="block bg-secondary transition-all duration-300 ease-out" style={{
        width: "1.5px",
        height: "12px"
      }} />
      </span>
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-secondary transition-all duration-300 ease-out group-hover:w-[20px] group-hover:h-[1.5px]" style={{
        width: "12px",
        height: "1.5px"
      }} />
      </span>
      <span aria-hidden="true" className="pointer-events-none absolute top-3 left-3 transition-all duration-300 ease-out group-hover:top-2 group-hover:left-2">
        <span className="block bg-secondary transition-all duration-300 ease-out group-hover:w-[1.5px] group-hover:h-[20px]" style={{
        width: "12px",
        height: "12px"
      }} />
      </span>

      {}
      <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 transition-all duration-300 ease-out group-hover:bottom-2 group-hover:right-2">
        <span className="block bg-secondary transition-all duration-300 ease-out group-hover:w-[20px] group-hover:h-[1.5px] ml-auto" style={{
        width: "12px",
        height: "1.5px"
      }} />
        <span className="block bg-secondary transition-all duration-300 ease-out group-hover:w-[1.5px] group-hover:h-[20px] ml-auto" style={{
        width: "12px",
        height: "12px"
      }} />
      </span>

      {}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 border border-border transition-all duration-300 ease-out group-hover:border-[2.5px] group-hover:border-secondary" />

      <div className="relative flex flex-col h-full min-h-[340px]">
        {}
        <div className="mb-8">
          <Icon className="text-foreground transition-colors duration-300 ease-out group-hover:text-secondary" strokeWidth={1.5} />
        </div>

        {}
        <div className="mb-4">
          <span className="font-default text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">
            {code}
          </span>
        </div>

        {}
        <h3 className="font-default uppercase text-foreground mb-5" style={{
        fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
        lineHeight: 0.92,
        letterSpacing: "0em"
      }}>
          {title}
        </h3>

        {}
        <p className="text-muted-foreground mb-8 flex-grow" style={{
        fontFamily: "var(--font-default)",
        fontSize: "0.9375rem",
        lineHeight: 1.65
      }}>
          {description}
        </p>

        {}
        <div className="flex items-end justify-between mt-auto pt-6 border-t border-border">
          <Link to="#" className="relative inline-flex items-center gap-2 group/link text-secondary hover:text-secondary font-default uppercase tracking-[0.04em] text-sm">
            <span className="relative">
              LEARN MORE
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-secondary transition-all duration-[220ms] ease-out group-hover/link:w-full" />
            </span>
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
          <span className="font-default text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium">
            {duration}
          </span>
        </div>
      </div>
    </div>;
}